import axios, { type AxiosInstance } from 'axios';
import type { ApiEnvelope } from '@pocketbuy/outdoor-business-types';

export type TokenProvider = () => string | undefined;

export class ApiError<T=unknown> extends Error {
  constructor(public readonly code:string,message:string,public readonly data:T,public readonly status?:number,public readonly traceId?:string){super(message);this.name='ApiError'}
}

export function createApiClient(baseURL: string, getToken: TokenProvider, onUnauthorized?: () => void): AxiosInstance {
  const client = axios.create({ baseURL, timeout: 15_000 });
  client.interceptors.request.use((config) => {
    const token = getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    const requestId = crypto.randomUUID();
    config.headers['X-Request-Id'] = requestId;
    if (config.method?.toUpperCase() === 'POST') config.headers['X-Idempotency-Key'] = requestId;
    return config;
  });
  client.interceptors.response.use((response)=>response,(error)=>{
    if(error?.response?.status===401)onUnauthorized?.();
    return Promise.reject(error);
  });
  return client;
}

export async function unwrap<T>(request: Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  try {
    const response = await request;
    if (response.data.code !== 'SUCCESS') throw new ApiError(response.data.code,response.data.message,response.data.data,undefined,response.data.traceId);
    return response.data.data;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (axios.isAxiosError(error)) {
      const envelope=error.response?.data as Partial<ApiEnvelope<unknown>>|undefined;
      if(envelope?.code&&envelope.code!=='SUCCESS')throw new ApiError(envelope.code,envelope.message??'Request failed',envelope.data,error.response?.status,envelope.traceId);
    }
    throw error;
  }
}
